import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { NbToastrService } from '@nebular/theme';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import {
  GridComponent,
  PageService, SortService, FilterService, ResizeService,
  ToolbarService, ColumnChooserService, ExcelExportService
} from '@syncfusion/ej2-angular-grids';

import { OperationService, OperationData, VarietySlice } from './operation.service';
import { FarmDTO } from '../../models/farm';
import { FarmFeature } from '../../../modules/farm-features/models/farm-feature-dto';
import { TenantWordsPipe } from '../../pipes/tenant-words.pipe';
import { KragworksEJSConstants } from '../../../../@core/utils/ejs-constants';
import { FarmsService } from '../../services/farms.service';
import { SessionService } from '../../../../@core/utils/session.service';
import { ErrorService } from '../../../../@core/utils/error.service';
import { FarmDetailComponent } from '../farms/farm-detail/farm-detail.component';
import { ViewMode } from './widgets/kw-view-toggle/kw-view-toggle.component';

interface FarmRow {
  id: number;
  name: string;
  customerName: string;
  location: string;
  acres: number;
  blockCount: number;
  assetCount: number;
  varietyCount: number;
}

/**
 * Operation Overview — the unified landing page for farm management.
 * Summary tiles for the whole operation, then farm cards (or the existing
 * farms table) that drill into a per-farm hub.
 */
@Component({
  selector: 'kw-operation-overview',
  templateUrl: './operation-overview.component.html',
  styleUrls: ['./operation-overview.component.scss'],
  providers: [
    TenantWordsPipe,
    PageService, SortService, FilterService, ResizeService,
    ToolbarService, ColumnChooserService, ExcelExportService
  ]
})
export class OperationOverviewComponent implements OnInit {
  @ViewChild('farmsGrid') farmsGrid: GridComponent;
  @ViewChild('farmNameTemplate', { static: true }) farmNameTemplate: any;
  @ViewChild('farmOpenTemplate', { static: true }) farmOpenTemplate: any;

  isLoading = true;
  farms: FarmDTO[] = [];
  features: FarmFeature[] = [];

  farmRows: FarmRow[] = [];
  farmColumns: any[] = [];

  view: ViewMode = 'cards';
  readonly viewStorageKey = 'operation-overview-farms-view';

  faEye = faEye;
  filterSettings = KragworksEJSConstants.filterSettings;
  pageSettings = KragworksEJSConstants.pageSettings;
  toolbar: any[] = ['Search', 'ColumnChooser', 'ExcelExport'];
  gridHeight = 560;

  totalFarms = 0;
  totalBlocks = 0;
  totalAcres = 0;
  totalAssets = 0;
  totalVarieties = 0;
  totalCrops = 0;

  constructor(
    private operationService: OperationService,
    private router: Router,
    private tenantWordsPipe: TenantWordsPipe,
    private dialog: MatDialog,
    private farmsService: FarmsService,
    private session: SessionService,
    private errorService: ErrorService,
    private toastr: NbToastrService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.operationService.loadOperation().subscribe({
      next: data => {
        this.apply(data);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  private apply(data: OperationData): void {
    this.farms = data.farms || [];
    // Drop inactive blocks so counts / acreage / variety breakdowns don't include them.
    this.farms.forEach(f => {
      if (f.blocks) {
        f.blocks = f.blocks.filter(b => b.isActive !== false);
      }
    });
    this.features = (data.features || []).filter(f => f.isActive !== false);

    this.totalFarms = this.farms.length;
    this.totalBlocks = this.farms.reduce((s, f) => s + (f.blocks ? f.blocks.length : 0), 0);
    this.totalAcres = this.farms.reduce((s, f) => s + this.operationService.farmAcres(f), 0);
    this.totalAssets = this.features.length;

    const varieties = new Set<string>();
    const crops = new Set<string>();
    this.farms.forEach(f => (f.blocks || []).forEach(b => {
      if (b.varietyName) { varieties.add(b.varietyName); }
      if (b.cropName) { crops.add(b.cropName); }
    }));
    this.totalVarieties = varieties.size;
    this.totalCrops = crops.size;

    this.farmRows = this.farms.map(f => ({
      id: f.id,
      name: f.name,
      customerName: f.customerName || '',
      location: [f.city, f.state].filter(Boolean).join(', '),
      acres: this.operationService.farmAcres(f),
      blockCount: f.blocks ? f.blocks.length : 0,
      assetCount: this.operationService.featuresForFarm(this.features, f.id).length,
      varietyCount: this.operationService.varietySplit(f).length
    }));
    this.setFarmColumns();
  }

  private setFarmColumns(): void {
    this.farmColumns = [
      { field: 'id', isPrimaryKey: true, visible: false },
      { field: 'name', headerText: this.tenantWordsPipe.transform('Farm'), template: this.farmNameTemplate, width: 220 },
      { field: 'customerName', headerText: this.tenantWordsPipe.transform('Customer'), width: 160 },
      { field: 'location', headerText: 'Location', width: 150 },
      { field: 'acres', headerText: 'GPS ' + this.tenantWordsPipe.transform('Acres'), width: 120, format: 'N1', textAlign: 'Right' },
      { field: 'blockCount', headerText: this.tenantWordsPipe.transform('Blocks'), width: 100, textAlign: 'Right' },
      { field: 'assetCount', headerText: this.tenantWordsPipe.transform('Assets'), width: 100, textAlign: 'Right' },
      { field: 'varietyCount', headerText: 'Varieties', width: 110, textAlign: 'Right' },
      {
        headerText: 'Open', template: this.farmOpenTemplate, width: 90,
        allowFiltering: false, allowSorting: false, textAlign: 'Center'
      }
    ];
  }

  acresFor(farm: FarmDTO): number {
    return this.operationService.farmAcres(farm);
  }

  blockCountFor(farm: FarmDTO): number {
    return farm.blocks ? farm.blocks.length : 0;
  }

  assetCountFor(farm: FarmDTO): number {
    return this.operationService.featuresForFarm(this.features, farm.id).length;
  }

  varietiesFor(farm: FarmDTO): VarietySlice[] {
    return this.operationService.varietySplit(farm);
  }

  openFarm(farm: FarmDTO): void {
    this.openFarmById(farm.id);
  }

  openFarmById(id: number): void {
    this.router.navigateByUrl(`harvest-engine/core/operation-overview/farm/${id}`);
  }

  onFarmRowClick(args: any): void {
    const id = args?.rowData?.id;
    if (id != null) {
      this.openFarmById(id);
    }
  }

  farmsToolbarClick(args: any): void {
    if (args?.item?.id && args.item.id.indexOf('excelexport') !== -1) {
      this.farmsGrid.excelExport({ fileName: 'Farms.xlsx' });
    }
  }

  newFarm(): void {
    // Farms are created via the FarmDetailComponent dialog (it closes through a
    // MatDialogRef), not the standalone route — matching the farms list.
    this.session.selectedFarm = {};
    const ref = this.dialog.open(FarmDetailComponent, { width: '1200px' });
    ref.afterClosed().subscribe(result => {
      if (result && result.farm) {
        this.farmsService.apiFarmsPost(result.farm).subscribe({
          next: () => {
            this.toastr.success(result.farm.name + ' created!', 'Farm added');
            this.load();
          },
          error: err => this.errorService.handleError(err)
        });
      }
      this.session.selectedFarm = {};
    });
  }

  onViewChange(view: ViewMode): void {
    this.view = view;
  }
}
